import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  Edit,
  History,
  Memory as MemoryIcon,
  CheckCircleOutline,
  Schedule,
  Memory,
  Psychology,
  RecordVoiceOver,
  FlagCircle,
  Build,
  Hub as HubIcon,
  MenuBook as KBIcon,
  TrendingUp as SalesIcon,
  Search as ResearchIcon,
  Mic as MeetingsIcon,
  Assignment as DeliveryIcon,
  SmartToy as AgentIcon,
} from "@mui/icons-material";
import { useAgent } from "@/hooks/useAgents";
import { brand } from "@/theme";

const AGENT_ICONS: Record<string, React.ReactNode> = {
  "AI-00": <HubIcon sx={{ fontSize: 32 }} />,
  "AI-01": <KBIcon sx={{ fontSize: 32 }} />,
  "AI-02": <SalesIcon sx={{ fontSize: 32 }} />,
  "AI-03": <ResearchIcon sx={{ fontSize: 32 }} />,
  "AI-04": <MeetingsIcon sx={{ fontSize: 32 }} />,
  "AI-05": <DeliveryIcon sx={{ fontSize: 32 }} />,
};

const AGENT_COLORS: Record<string, string> = {
  "AI-00": brand.forestGreen,
  "AI-01": "#2E7D32",
  "AI-02": "#C4922A",
  "AI-03": "#1565C0",
  "AI-04": "#6A1B9A",
  "AI-05": "#E65100",
};

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          {icon}
          <Typography variant="h6">{title}</Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );
}

function formatCron(cron: string): string {
  const parts = cron.split(" ");
  const hour = parts[1];
  const dow = parts[4];
  const dowMap: Record<string, string> = {
    "1-5": "Weekdays",
    "1,4": "Monday & Thursday",
    "1": "Mondays",
    "*": "Daily",
  };
  return `${hour}:00 — ${dowMap[dow ?? ""] ?? dow}`;
}

export default function AgentDetail() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { data: agent, isLoading, error } = useAgent(agentId);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !agent) {
    return <Alert severity="error">Agent not found.</Alert>;
  }

  const color = AGENT_COLORS[agent.code] ?? brand.forestGreen;

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/agents")}>
          Back to Agents
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={<MemoryIcon />}
          onClick={() => navigate(`/agents/${agentId}/memory`)}
        >
          Memory
        </Button>
        <Button
          variant="outlined"
          startIcon={<History />}
          onClick={() => navigate(`/agents/${agentId}/history`)}
        >
          History
        </Button>
        <Button
          variant="contained"
          startIcon={<Edit />}
          onClick={() => navigate(`/agents/${agentId}/edit`)}
        >
          Edit
        </Button>
      </Box>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
        <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
          {AGENT_ICONS[agent.code] ?? <AgentIcon sx={{ fontSize: 32 }} />}
        </Avatar>
        <Box>
          <Typography variant="overline" color="text.secondary">
            {agent.code}
          </Typography>
          <Typography variant="h4">{agent.name}</Typography>
        </Box>
        <Box sx={{ ml: "auto" }}>
          <Chip
            label={agent.is_active ? "Active" : "Inactive"}
            color={agent.is_active ? "success" : "default"}
          />
        </Box>
      </Box>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 3, fontStyle: "italic" }}>
        &ldquo;{agent.tagline}&rdquo;
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        {/* Left column */}
        <Box>
          {/* Role */}
          <Section title="Role" icon={<FlagCircle color="action" />}>
            <Typography variant="body1">{agent.role_description}</Typography>
          </Section>

          {/* Personality */}
          <Section title="Personality" icon={<Psychology color="action" />}>
            <Typography variant="body1">{agent.personality}</Typography>
          </Section>

          {/* Communication Style */}
          <Section title="Communication Style" icon={<RecordVoiceOver color="action" />}>
            <Typography variant="body1">{agent.communication_style}</Typography>
          </Section>
        </Box>

        {/* Right column */}
        <Box>
          {/* Goals */}
          {agent.goals && (
            <Section title="Goals" icon={<CheckCircleOutline color="action" />}>
              <List dense disablePadding>
                {agent.goals.map((goal, i) => (
                  <ListItem key={i} disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleOutline fontSize="small" sx={{ color: color }} />
                    </ListItemIcon>
                    <ListItemText primary={goal} />
                  </ListItem>
                ))}
              </List>
            </Section>
          )}

          {/* Skills */}
          {agent.skills && (
            <Section title="Skills" icon={<Build color="action" />}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {agent.skills.map((skill) => (
                  <Chip key={skill} label={skill} size="small" variant="outlined" />
                ))}
              </Box>
            </Section>
          )}

          {/* Model Config */}
          <Section title="Model Configuration" icon={<Memory color="action" />}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip label={`Provider: ${agent.model_provider}`} size="small" />
              <Chip label={`Model: ${agent.model_id}`} size="small" />
              {agent.model_config_json?.temperature != null && (
                <Chip
                  label={`Temp: ${agent.model_config_json.temperature}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {agent.model_config_json?.max_tokens != null && (
                <Chip
                  label={`Max tokens: ${agent.model_config_json.max_tokens}`}
                  size="small"
                  variant="outlined"
                />
              )}
              {agent.model_config_json?.escalation_model != null && (
                <Chip
                  label={`Escalation: ${String(agent.model_config_json.escalation_model)}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
          </Section>

          {/* Heartbeat */}
          {agent.heartbeat_cron && (
            <Section title="Heartbeat Schedule" icon={<Schedule color="action" />}>
              <Chip
                label={formatCron(agent.heartbeat_cron)}
                sx={{ mb: 1.5 }}
                color="primary"
                variant="outlined"
              />
              {agent.heartbeat_tasks && (
                <List dense disablePadding>
                  {agent.heartbeat_tasks.map((task, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Schedule fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={task.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Section>
          )}
        </Box>
      </Box>
    </Box>
  );
}
